import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, FlatList, Alert, ScrollView } from 'react-native';
import { Card, Button, TextInput, ActivityIndicator, FAB, Divider, IconButton, RadioButton, Switch } from 'react-native-paper';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import api from '../../services/api';
import HelpBanner from '../../components/HelpBanner';

const ConfigComisionesScreen = () => {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRegla, setEditingRegla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const [form, setForm] = useState({
    nombre: '',
    tipo: 'global',
    producto_id: '',
    categoria_id: '',
    porcentaje: '',
    monto_fijo: '',
    prioridad: '0',
  });

  useEffect(() => {
    loadData();
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [navigation]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reglasData, productosData, categoriasData] = await Promise.all([
        api.getComisionReglas(),
        api.getProductos(),
        api.getCategorias(),
      ]);
      setReglas(reglasData);
      setProductos(Array.isArray(productosData) ? productosData : []);
      setCategorias(Array.isArray(categoriasData) ? categoriasData : []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ nombre: '', tipo: 'global', producto_id: '', categoria_id: '', porcentaje: '', monto_fijo: '', prioridad: '0' });
    setEditingRegla(null);
    setShowForm(false);
  };

  const handleEdit = (regla) => {
    setForm({
      nombre: regla.nombre,
      tipo: regla.tipo,
      producto_id: regla.producto_id ? regla.producto_id.toString() : '',
      categoria_id: regla.categoria_id ? regla.categoria_id.toString() : '',
      porcentaje: regla.porcentaje.toString(),
      monto_fijo: regla.monto_fijo.toString(),
      prioridad: (regla.prioridad || 0).toString(),
    });
    setEditingRegla(regla);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) {
      Alert.alert('Error', 'El nombre es requerido');
      return;
    }
    if (!form.porcentaje && !form.monto_fijo) {
      Alert.alert('Error', 'Defina porcentaje o monto fijo');
      return;
    }

    try {
      setSaving(true);
      const data = {
        nombre: form.nombre,
        tipo: form.tipo,
        producto_id: form.tipo === 'producto' ? parseInt(form.producto_id) || null : null,
        categoria_id: form.tipo === 'categoria' ? parseInt(form.categoria_id) || null : null,
        porcentaje: parseFloat(form.porcentaje) || 0,
        monto_fijo: parseFloat(form.monto_fijo) || 0,
        prioridad: parseInt(form.prioridad) || 0,
        activo: 1,
      };

      if (editingRegla) {
        await api.updateComisionRegla(editingRegla.id, data);
      } else {
        await api.createComisionRegla(data);
      }

      resetForm();
      loadData();
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (regla) => {
    Alert.alert('Desactivar Regla', `¿Desactivar "${regla.nombre}"?`, [
      { text: 'Cancelar', style: 'cancel' },
      {
        text: 'Desactivar', style: 'destructive', onPress: async () => {
          try {
            await api.deleteComisionRegla(regla.id);
            loadData();
          } catch (error) {
            Alert.alert('Error', error.message);
          }
        }
      }
    ]);
  };

  const renderRegla = ({ item }) => (
    <Card style={[styles.card, !item.activo && styles.cardInactive]}>
      <Card.Content>
        <View style={styles.reglaHeader}>
          <View style={styles.reglaInfo}>
            <Text style={styles.reglaNombre}>{item.nombre}</Text>
            <View style={[styles.tipoBadge, { backgroundColor: item.tipo === 'global' ? '#007bff' : item.tipo === 'categoria' ? '#6F42C1' : '#17A2B8' }]}>
              <Text style={styles.tipoText}>{item.tipo.toUpperCase()}</Text>
            </View>
          </View>
          <View style={styles.reglaActions}>
            <IconButton icon="pencil" size={18} onPress={() => handleEdit(item)} />
            {item.activo ? (
              <IconButton icon="delete" size={18} iconColor="#DC3545" onPress={() => handleDelete(item)} />
            ) : null}
          </View>
        </View>
        {item.producto_nombre && <Text style={styles.reglaDetalle}>Producto: {item.producto_nombre}</Text>}
        {item.categoria_nombre && <Text style={styles.reglaDetalle}>Categoría: {item.categoria_nombre}</Text>}
        <View style={styles.reglaValores}>
          {parseFloat(item.porcentaje) > 0 && <Text style={styles.reglaValor}>{item.porcentaje}%</Text>}
          {parseFloat(item.monto_fijo) > 0 && <Text style={styles.reglaValor}>+ L.{parseFloat(item.monto_fijo).toFixed(2)}/unidad</Text>}
        </View>
        {!item.activo && <Text style={styles.inactiveText}>INACTIVA</Text>}
      </Card.Content>
    </Card>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007bff" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <HelpBanner title="¿Cómo se calculan las comisiones?">
        <Text style={styles.helpText}>
          Cuando una venta se completa, el sistema busca la regla más específica que aplique
          y la usa para calcular la comisión del vendedor:
        </Text>
        <Text style={styles.helpItem}>
          • <Text style={styles.helpStrong}>Por Producto</Text> — gana si la venta incluye ese producto exacto. Es la más específica.
        </Text>
        <Text style={styles.helpItem}>
          • <Text style={styles.helpStrong}>Por Categoría</Text> — aplica a todos los productos de esa categoría. Solo se usa si no hubo regla por producto.
        </Text>
        <Text style={styles.helpItem}>
          • <Text style={styles.helpStrong}>Global</Text> — la regla por defecto, aplica a cualquier producto que no tenga regla más específica.
        </Text>
        <Text style={[styles.helpText, { marginTop: 8 }]}>
          <Text style={styles.helpStrong}>Porcentaje vs Monto fijo:</Text> podés definir uno o ambos. Si pones los dos, se cobra la suma (ej. 5% del subtotal + L. 10 fijo por unidad vendida).
        </Text>
        <Text style={[styles.helpText, { marginTop: 8 }]}>
          <Text style={styles.helpStrong}>Prioridad:</Text> si hay dos reglas que aplican al mismo nivel, gana la de prioridad más alta.
        </Text>
      </HelpBanner>
      {showForm && (
        <ScrollView style={styles.formContainer}>
          <Card style={styles.card}>
            <Card.Title title={editingRegla ? 'Editar Regla' : 'Nueva Regla de Comisión'} />
            <Card.Content>
              <TextInput label="Nombre*" value={form.nombre} onChangeText={v => setForm({ ...form, nombre: v })} mode="outlined" style={styles.input} />

              <Text style={styles.fieldLabel}>Tipo de regla:</Text>
              <RadioButton.Group onValueChange={v => setForm({ ...form, tipo: v })} value={form.tipo}>
                <RadioButton.Item label="Global (todos los productos)" value="global" />
                <RadioButton.Item label="Por Categoría" value="categoria" />
                <RadioButton.Item label="Por Producto" value="producto" />
              </RadioButton.Group>

              {form.tipo === 'categoria' && (
                <TextInput
                  label="ID Categoría"
                  value={form.categoria_id}
                  onChangeText={v => setForm({ ...form, categoria_id: v })}
                  mode="outlined"
                  keyboardType="numeric"
                  style={styles.input}
                  right={<TextInput.Affix text={categorias.find(c => c.id === parseInt(form.categoria_id))?.nombre || ''} />}
                />
              )}

              {form.tipo === 'producto' && (
                <TextInput
                  label="ID Producto"
                  value={form.producto_id}
                  onChangeText={v => setForm({ ...form, producto_id: v })}
                  mode="outlined"
                  keyboardType="numeric"
                  style={styles.input}
                  right={<TextInput.Affix text={productos.find(p => p.id === parseInt(form.producto_id))?.nombre || ''} />}
                />
              )}

              <TextInput label="Porcentaje (%)" value={form.porcentaje} onChangeText={v => setForm({ ...form, porcentaje: v })} mode="outlined" keyboardType="decimal-pad" style={styles.input} />
              <TextInput label="Monto fijo por unidad (L.)" value={form.monto_fijo} onChangeText={v => setForm({ ...form, monto_fijo: v })} mode="outlined" keyboardType="decimal-pad" style={styles.input} />
              <TextInput label="Prioridad" value={form.prioridad} onChangeText={v => setForm({ ...form, prioridad: v })} mode="outlined" keyboardType="numeric" style={styles.input} />

              <View style={styles.formButtons}>
                <Button mode="outlined" onPress={resetForm} style={styles.formButton}>Cancelar</Button>
                <Button mode="contained" onPress={handleSave} loading={saving} disabled={saving} style={styles.formButton}>
                  {editingRegla ? 'Actualizar' : 'Crear'}
                </Button>
              </View>
            </Card.Content>
          </Card>
        </ScrollView>
      )}

      {!showForm && (
        <>
          <FlatList
            data={reglas}
            keyExtractor={(item) => item.id.toString()}
            renderItem={renderRegla}
            ListEmptyComponent={<Text style={styles.emptyText}>No hay reglas configuradas</Text>}
            contentContainerStyle={reglas.length === 0 ? styles.emptyContainer : { paddingBottom: 80 }}
          />
          <FAB
            style={[styles.fab, { marginBottom: insets.bottom }]}
            icon="plus"
            onPress={() => setShowForm(true)}
          />
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  formContainer: { flex: 1 },
  card: { margin: 8, elevation: 2 },
  cardInactive: { opacity: 0.5 },
  input: { marginBottom: 8 },
  fieldLabel: { fontSize: 14, fontWeight: '600', color: '#333', marginTop: 8, marginBottom: 4 },
  formButtons: { flexDirection: 'row', justifyContent: 'flex-end', marginTop: 12, gap: 8 },
  formButton: { marginLeft: 8 },
  reglaHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  reglaInfo: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  reglaNombre: { fontSize: 15, fontWeight: 'bold', color: '#333', marginRight: 8 },
  tipoBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 10 },
  tipoText: { color: '#fff', fontSize: 10, fontWeight: '700' },
  reglaActions: { flexDirection: 'row' },
  reglaDetalle: { fontSize: 13, color: '#666', marginTop: 4 },
  reglaValores: { flexDirection: 'row', marginTop: 6, gap: 8 },
  reglaValor: { fontSize: 16, fontWeight: 'bold', color: '#28A745' },
  inactiveText: { color: '#DC3545', fontWeight: 'bold', marginTop: 4, fontSize: 12 },
  emptyText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 40 },
  emptyContainer: { flex: 1, justifyContent: 'center' },
  fab: { position: 'absolute', margin: 16, right: 0, bottom: 0, backgroundColor: '#007bff' },
  helpText: { fontSize: 12, color: '#333', lineHeight: 17 },
  helpStrong: { fontWeight: '700' },
  helpItem: { fontSize: 12, color: '#333', lineHeight: 18, marginTop: 2 },
});

export default ConfigComisionesScreen;
